import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Badge,
  Switch,
  FormControlLabel,
  alpha,
  useTheme
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationIcon,
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";

const mockNotifications = [
  {
    id: 1,
    title: "New Cleaning Service Available",
    message: "We've added deep cleaning services to our offerings",
    type: "Promotional",
    status: "Sent",
    audience: "All Customers",
    sentDate: "2024-03-21",
    sentTime: "10:30 AM",
    channels: ["Email", "Push"],
    opens: 1245,
    clicks: 234
  },
  {
    id: 2,
    title: "Booking Confirmation",
    message: "Your cleaning service has been confirmed for tomorrow",
    type: "Transactional",
    status: "Scheduled",
    audience: "Booked Customers",
    sentDate: "2024-03-22",
    sentTime: "9:00 AM",
    channels: ["Email", "SMS"],
    opens: 0,
    clicks: 0
  },
  {
    id: 3,
    title: "Weekly Cleaning Tips",
    message: "Check out our latest tips for maintaining a clean home",
    type: "Educational",
    status: "Draft",
    audience: "Active Users",
    sentDate: "-",
    sentTime: "-",
    channels: ["Email"],
    opens: 0,
    clicks: 0
  },
  {
    id: 4,
    title: "Payment Received",
    message: "Your payment for service #12345 has been processed",
    type: "Transactional",
    status: "Sent",
    audience: "Individual",
    sentDate: "2024-03-20",
    sentTime: "2:15 PM",
    channels: ["Email"],
    opens: 1,
    clicks: 1
  }
];

const NotificationTypes = {
  ALL: 'all',
  PROMOTIONAL: 'Promotional',
  TRANSACTIONAL: 'Transactional',
  EDUCATIONAL: 'Educational'
};

const StatusTypes = {
  ALL: 'all',
  SENT: 'Sent',
  SCHEDULED: 'Scheduled',
  DRAFT: 'Draft'
};

export default function Notifications() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [typeFilter, setTypeFilter] = useState(NotificationTypes.ALL);
  const [statusFilter, setStatusFilter] = useState(StatusTypes.ALL);

  const filteredNotifications = mockNotifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === NotificationTypes.ALL || notification.type === typeFilter;
    const matchesStatus = statusFilter === StatusTypes.ALL || notification.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedNotifications(filteredNotifications.map(n => n.id));
    } else {
      setSelectedNotifications([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedNotifications.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedNotifications, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedNotifications.slice(1));
    } else if (selectedIndex === selectedNotifications.length - 1) {
      newSelected = newSelected.concat(selectedNotifications.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedNotifications.slice(0, selectedIndex),
        selectedNotifications.slice(selectedIndex + 1),
      );
    }
    setSelectedNotifications(newSelected);
  };

  const isSelected = (id) => selectedNotifications.indexOf(id) !== -1;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Sent': return 'success';
      case 'Scheduled': return 'warning';
      case 'Draft': return 'default';
      default: return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Promotional': return 'primary';
      case 'Transactional': return 'success';
      case 'Educational': return 'info';
      default: return 'default';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'Email': return <EmailIcon fontSize="small" />;
      case 'SMS': return <SmsIcon fontSize="small" />;
      case 'Push': return <NotificationIcon fontSize="small" />;
      default: return <CampaignIcon fontSize="small" />;
    }
  };

  const stats = [
    { label: 'Total Sent', value: '1,245', change: '+12%' },
    { label: 'Open Rate', value: '68%', change: '+5%' },
    { label: 'Click Rate', value: '23%', change: '+3%' },
    { label: 'Scheduled', value: '12', change: '+2' },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Notifications
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Create Notification
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage and send notifications to customers and cleaners
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Chip 
                  label={stat.change} 
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls Section */}
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search notifications..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Filters
            </Button>

            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={() => setFilterMenuAnchor(null)}
            >
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Type:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[NotificationTypes.ALL, NotificationTypes.PROMOTIONAL, NotificationTypes.TRANSACTIONAL, NotificationTypes.EDUCATIONAL].map(type => (
                    <Chip
                      key={type}
                      label={type}
                      size="small"
                      color={typeFilter === type ? "primary" : "default"}
                      onClick={() => setTypeFilter(type)}
                    />
                  ))}
                </Box>
              </MenuItem>
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Status:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[StatusTypes.ALL, StatusTypes.SENT, StatusTypes.SCHEDULED, StatusTypes.DRAFT].map(status => (
                    <Chip
                      key={status}
                      label={status}
                      size="small"
                      color={statusFilter === status ? "primary" : "default"}
                      onClick={() => setStatusFilter(status)}
                    />
                  ))}
                </Box>
              </MenuItem>
            </Menu>

            <Box sx={{ flexGrow: 1 }} />

            <IconButton sx={{ borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>

            {selectedNotifications.length > 0 && (
              <Chip 
                label={`${selectedNotifications.length} selected`} 
                color="primary" 
                variant="outlined"
                onDelete={() => setSelectedNotifications([])}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Notifications Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedNotifications.length > 0 && selectedNotifications.length < filteredNotifications.length}
                    checked={filteredNotifications.length > 0 && selectedNotifications.length === filteredNotifications.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Notification</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Type</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Audience</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Channels</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Performance</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Sent Date</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNotifications
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((notification) => {
                  const isItemSelected = isSelected(notification.id);
                  return (
                    <TableRow
                      key={notification.id}
                      hover
                      selected={isItemSelected}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, notification.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">
                            {notification.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={notification.type} 
                          size="small"
                          color={getTypeColor(notification.type)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {notification.audience}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {notification.channels.map((channel, index) => (
                            <Badge key={index} badgeContent={getChannelIcon(channel)} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={notification.status}
                          size="small"
                          color={getStatusColor(notification.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {notification.status === 'Sent' ? (
                          <Box>
                            <Typography variant="body2">
                              Opens: <strong>{notification.opens}</strong>
                            </Typography>
                            <Typography variant="body2">
                              Clicks: <strong>{notification.clicks}</strong>
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not sent yet
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {notification.sentDate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {notification.sentTime}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredNotifications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Card>
    </Box>
  );
}